import React from "react";
import { useLocation, useNavigate,useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Download, ArrowLeft } from "lucide-react";
import "../styles.css";
import { apiUrl, normalizeAssetPath } from "../lib/api";
import toast from "react-hot-toast";

const Result = () => {
  const location = useLocation();
  // const params = useParams();
  const navigate = useNavigate();
  const { result,job_id} = location.state || {};
  
  const assetUrl = (p) => apiUrl(normalizeAssetPath(p));
  const handleDownload = async () => {
    try {

      toast.loading("Preparing download...");

      const formData = new FormData();
      formData.append("refseq_id", result.refseq_id);
      formData.append("job_id", job_id);
      console.log(result.refseq_id);
      console.log(job_id);
      console.log("hi");
      console.log(formData);
      const response = await fetch(apiUrl("/download_origami"), {
        method: "POST",
        body: formData,
      });
      const contentDisposition = response.headers.get("content-disposition");

      let filename = "download.zip";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/);
        if (match) filename = match[1];
      }

      console.log(filename);

      if (!response.ok) {
        throw new Error("Failed to download report");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success("Download will start shortly!");

    } catch (err) {
      toast.dismiss();
      toast.error("Download failed");

      console.error(err);
      alert("Download failed");
    }
  };

  if (!result)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center bg-white p-10 rounded-lg shadow border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            No Result Found
          </h2>
          <p className="text-gray-500">
            Please go back and upload a valid genome file.
          </p>
          <button
            onClick={() => {
              navigate("/search");
            }}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded-md transition"
          >
            <ArrowLeft size={16} />
            Back to Search
          </button>
        </div>
      </div>
    );

  const s = result.stats || {};

  // Utility to detect missing OR empty coordinates
  const isMissing = (val) =>
    !val || val === "Not found" || val === "None" || val === "";

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-slate-50 py-12 font-sans">
      <div className="max-w-6xl mx-auto px-6">
        {/* ===== HEADER ===== */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
          <motion.h1
            className="text-4xl font-bold text-center md:text-left text-slate-800 tracking-tight"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            OriC & DnaA Prediction Results
          </motion.h1>

          <motion.button
            onClick={() => {
              navigate("/search");
            }}
            className="mt-4 md:mt-0 inline-flex items-center gap-2 bg-gray-700 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800 transition self-center md:self-auto"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ArrowLeft size={16} />
            Back to Search
          </motion.button>
        </div>

        {/* ===== GENOME OVERVIEW ===== */}
        <motion.section
          className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-12"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 border-b border-gray-200 pb-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-800">
                Genome Overview
              </h2>
              <p className="text-sm text-gray-500">
                General information and genomic statistics.
              </p>
            </div>

            {result.refseq_id && (() => {
              const gcfId = result.refseq_id.match(/^GCF_\d+\.\d+/)?.[0];

              return (
                <a
                  href={`https://www.ncbi.nlm.nih.gov/datasets/genome/${gcfId}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded-md transition"
                >
                  <ExternalLink size={16} />
                  Open on NCBI
                </a>
              );
            })()}
          </div>

          {/* Overview Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-[15px] text-gray-800">
            <DataRow label="NCBI ID" value={result.refseq_id} isMono />
            <DataRow
              label="Original Genome Size"
              value={`${s.total_bp?.toLocaleString() || 0} bp`}
              isMono
            />
            <DataRow
              label="Trimmed Genome Size"
              value={`${s.trimmed_genome_size_bp?.toLocaleString() || 0} bp`}
              isMono
            />
            <DataRow
              label="Trimmed Portion"
              value={`${s.trimmed_portion_bp?.toLocaleString() || 0} bp`}
              isMono
            />
            <DataRow label="GC Content" value={`${s.gc_percent}%`} />
            <DataRow label="Chromosomes" value={s.chromosomes} isMono />
            <DataRow label="Contigs" value={s.contigs} isMono />
            <DataRow label="Plasmids" value={s.plasmids} isMono />
          </div>

          {/* Buttons */}
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-md text-sm hover:bg-emerald-800 transition"
            >
              <Download size={16} />
              Download Report
            </button>

            {/* {result.download_rotated_fasta && (
              <a
                href={assetUrl(result.download_rotated_fasta)}
                className="flex items-center gap-2 bg-slate-700 text-white px-4 py-2 rounded-md text-sm hover:bg-slate-800 transition"
              >
                <Download size={16} />
                Rotated FASTA
              </a>
            )} */}
          </div>
        </motion.section>

        {/* ===== REPLICON SECTIONS ===== */}
        {result.replicons.map((rep, idx) => {
          const missingOriC = isMissing(rep.oriC_coords);
          const missingDnaA = isMissing(rep.dnaA_coords);

          if (missingOriC && missingDnaA) {
            return (
              <motion.section
                key={idx}
                className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-12"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="mb-6 border-b border-gray-200 pb-3 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-800">
                      {rep.organism_name}
                    </h2>
                    <p className="text-sm text-gray-500">{rep.nc_id}</p>
                  </div>
                  <span className="text-xs text-gray-500 italic mt-3 md:mt-0">
                    Replicon {idx + 1} of {result.replicons.length}
                  </span>
                </div>

                <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-md text-center text-yellow-700 font-medium">
                  This replicon does not contain detectable OriC or DnaA
                  regions.
                </div>
              </motion.section>
            );
          }

          return (
            <motion.section
              key={idx}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-12"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="mb-6 border-b border-gray-200 pb-3 flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-800">
                    {rep.organism_name}
                  </h2>
                  <p className="text-sm text-gray-500">{rep.nc_id}</p>
                </div>
                <span className="text-xs text-gray-500 italic mt-3 md:mt-0">
                  Replicon {idx + 1} of {result.replicons.length}
                </span>
              </div>

              {/* ---- GC PLOTS ---- */}
              <h3 className="text-base font-semibold text-slate-700 mb-3 border-b border-gray-200 pb-1">
                GC Skew Circos Plots
              </h3>

              {(() => {
                const genomeSize =
                  s.replicon_lengths_bp[rep.nc_id];

                // This shift MUST be chosen so that OriC start maps to position 1
                const shift = rep.rotated_shift_bp;

                const parseIntervals = (coordStr) => {
                  if (!coordStr) return [];
                  return coordStr
                    .split(/,|and/)
                    .map(part => {
                      const [start, end] = part
                        .split("-")
                        .map(n => parseInt(n.trim(), 10));
                      return { start, end };
                    });
                };

                const intervalLength = ({ start, end }) => {
                  if (start <= end) return end - start + 1;
                  return (genomeSize - start + 1) + end;
                };

                const totalFeatureLength = (intervals) =>
                  intervals.reduce((sum, iv) => sum + intervalLength(iv), 0);

                const rotatePos = (pos) =>
                  ((pos - shift + genomeSize) % genomeSize) + 1

                const rotateCoords = (coordStr, featureType) => {
                  if (!coordStr) return "-";

                  const intervals = parseIntervals(coordStr);
                  if (!intervals.length) return "-";

                  const totalLen = totalFeatureLength(intervals);

                  // Anchor OriC explicitly
                  if (featureType === rep.rotated_from) {
                    return `1 - ${totalLen}`;
                  }

                  let rotated = [];
                  for (const { start, end } of intervals) {
                    const rs = rotatePos(start);
                    const re = rotatePos(end);

                    if (rs <= re) {
                      rotated.push({ start: rs, end: re });
                    } else {
                      rotated.push({ start: 1, end: re });
                      rotated.push({ start: rs, end: genomeSize });
                    }
                  }

                  // Sort + merge
                  rotated.sort((a, b) => a.start - b.start);

                  const merged = [];
                  for (const iv of rotated) {
                    if (
                      merged.length &&
                      merged[merged.length - 1].end + 1 >= iv.start
                    ) {
                      merged[merged.length - 1].end = Math.max(
                        merged[merged.length - 1].end,
                        iv.end
                      );
                    } else {
                      merged.push({ ...iv });
                    }
                  }

                  return merged.map(iv => `${iv.start} - ${iv.end}`).join(", ");
                };

                /* ------------------ trim adjust ------------------ */

                const adjustCoords = (coordStr) => {
                  if (!coordStr) return "-";

                  const intervals = parseIntervals(coordStr).map(({ start, end }) => ({
                    start: start + s.trimmed_portion_bp,
                    end: end + s.trimmed_portion_bp,
                  }));

                  return intervals.map(iv => `${iv.start} - ${iv.end}`).join(", ");
                };

                /* ------------------ outputs ------------------ */

                const rotatedOriC = rotateCoords(rep.oriC_coords, "oric");
                const rotatedDnaA = rotateCoords(rep.dnaA_coords, "dnaA");

                const adjustDnaA = adjustCoords(rep.dnaA_coords);

                /* ------------------ render ------------------ */

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {/* ORIGINAL */}
                    {rep.original_plots?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700">
                          ORIGINAL GENOME
                        </h4>
                        <div className="border-b border-gray-200 mt-1 mb-3"></div>

                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">DnaA Coordinates</span>
                          <span className="font-mono">{rep.dnaA_coords}</span>
                        </div>

                        <div className="flex justify-between text-sm mb-4">
                          <span className="font-medium">OriC Coordinates</span>
                          <span className="font-mono">{rep.oriC_coords}</span>
                        </div>

                        <div className="flex flex-col items-center">
                          <img
                          src={assetUrl(rep.original_plots[0])}
                          alt="Original Genome"
                          className="rounded border mx-auto max-h-[430px]"
                        />
                        <p className="text-xs text-gray-500 mt-2 italic text-center max-w-[430px]">
                            The <span className="text-red-500 font-medium">oriC</span> is shown as a red circle,
                            and the <span className="text-blue-500 font-medium">dnaA</span> is shown as a blue square.
                          </p>
                       </div>
                      </div>
                    )}

                    {/* ROTATED */}
                    {rep.rotated_plots?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700">
                          ROTATED (ORIGIN ALIGNED)
                        </h4>
                        <div className="border-b border-gray-200 mt-1 mb-3"></div>

                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">Rotated DnaA</span>
                          <span className="font-mono">{rotatedDnaA}</span>
                        </div>

                        <div className="flex justify-between text-sm mb-4">
                          <span className="font-medium">Rotated OriC</span>
                          <span className="font-mono">{rotatedOriC}</span>
                        </div>

                        <div className="flex flex-col items-center">
                          <img
                            src={assetUrl(rep.rotated_plots[0])}
                            alt="Rotated Genome"
                            className="rounded border max-h-[430px]"
                          />

                          <p className="text-xs text-gray-500 mt-2 italic text-center max-w-[430px]">
                            The <span className="text-red-500 font-medium">oriC</span> is shown as a red circle,
                            and the <span className="text-blue-500 font-medium">dnaA</span> is shown as a blue square.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}


              {/* DOT PLOT */}
              {rep.dot_plots?.length > 0 && (
                <div className="mt-10">
                  <h3 className="text-base font-semibold text-slate-700 mb-3 border-b border-gray-200 pb-1">
                    Dot Plot Visualization
                  </h3>

                  <div className="bg-white border border-gray-200 rounded-md p-4 text-center">
                    <img
                      src={assetUrl(rep.dot_plots[0])}
                      alt="Dot Plot"
                      className="max-h-[500px] mx-auto rounded border border-gray-200 object-contain"
                    />
                    <p className="text-xs text-gray-500 mt-2 italic">
                      Darker regions represent higher similarity.
                    </p>
                  </div>
                </div>
              )}

              {/* PGV Plot */}
              {rep.pgv_plot && (
                <div className="mt-10">
                  <h3 className="text-base font-semibold text-slate-700 mb-3 border-b border-gray-200 pb-1">
                    PGV-PMauve Comparative Genome Visualization
                  </h3>

                  <div className="bg-white border border-gray-200 rounded-md p-4 text-center">
                    <img
                      src={assetUrl(rep.pgv_plot)}
                      alt="PGV Plot"
                      className="max-h-[500px] mx-auto rounded border border-gray-200 object-contain"
                    />
                    <p className="text-xs text-gray-500 mt-2 italic">
                      Colored synteny blocks indicate homologous regions.
                    </p>
                  </div>
                </div>
              )}
            </motion.section>
          );
        })}
      </div>
    </div>
  );
};

const DataRow = ({ label, value, isMono }) => (
  <div className="flex justify-between border-b border-gray-100 pb-1">
    <span className="font-medium text-slate-700">{label}</span>
    <span
      className={`${isMono ? "font-mono text-[13px]" : "text-gray-800"
        } text-right`}
    >
      {value || "-"}
    </span>
  </div>
);

export default Result;
